import React, { useState, useEffect, useContext } from "react";
import { TouchableOpacity, Modal } from "react-native";
import styled from 'styled-components/native';
import _ from "lodash";
import moment from "moment";

import { BasicText, SmallLabel } from "../Styles";
import Icon from "./Icon";
import { DataContext } from 'opidcore';

const PeriodPicker = ({field1, field2 = undefined, label, dateFormat = "YYYY-MM", flexDirection = "column", style = undefined, LabelType = SmallLabel, disabledMonths = []}) => {
    const boundMagic = useContext(DataContext);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(()=>{
        setStartDate(boundMagic.magicalGet(field1, null));
        if(field2 != undefined){
            setEndDate(boundMagic.magicalGet(field2, null));
        }
    }, [boundMagic]);
    
    const updateBound = (val) => {
        if(val[0] != null){
            boundMagic.magicalSet(field1, val[0]);
        }

        if(field2 != undefined && val[1] != null){
            boundMagic.magicalSet(field2, val[1]);
        }
        
        setStartDate(val[0]);
        if(field2 != undefined){
            setEndDate(val[1]);
        }
    }

    const defaultStyle = {
        display: "flex",
        flexDirection: flexDirection,
        alignItems: "center"
    }

    const calendar = <PeriodSelectDropdown dateRange={[startDate, endDate]} field2={field2} updateDateRange={updateBound} setModalVisible={(val)=>setOpen(val)} modalVisible={open}/>;

    let displayText = null;

    if(startDate != null && ( (endDate != null && startDate.isSame(endDate, "day")) || field2 == undefined )){
        displayText = startDate.format(dateFormat);
    } else {
        displayText = (startDate == null? "N/A" : startDate.format(dateFormat)) + " to " + (endDate == null? "N/A" : endDate.format(dateFormat));
    }

    return <TouchableOpacity style={[defaultStyle, style]} onPress={() => setOpen(true)} activeOpacity={1}>
        {open? calendar : null}
        <LabelType>{label}</LabelType>
        <DisplayText><BasicText>{displayText}</BasicText></DisplayText>
    </TouchableOpacity>;
}

const PeriodSelectDropdown = ({dateRange, field2, updateDateRange, setModalVisible, modalVisible}) => {
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [currentDateRange, setCurrentDateRange] = useState([null, null]);

    useEffect(()=>{
        setCurrentDateRange(dateRange);
        setSelectedYear(parseInt(moment().format("YYYY")));
    }, []);

    useEffect(()=>{
        if(selectedMonth != null){
            updateDate(selectedMonth);
        }
    }, [selectedMonth]);

    useEffect(()=>{
        if(selectedMonth != null && field2 == undefined){
            completeSelection();
        }
    }, [currentDateRange]);

    const decrementPeriod = () => {
        setSelectedYear(selectedYear - 1);
    };

    const incrementPeriod = () => {
        setSelectedYear(selectedYear + 1);
    };

    const updateDate = (month) => {
        const val = moment();
        val.set("date", 1);
        val.set("month", month);
        if(selectedYear != undefined){
            val.set("year", selectedYear);
        }

        const newDateRange = [currentDateRange[0], currentDateRange[1]];

        if(field2 == undefined){
            newDateRange[0] = val;
        } else {
            if(newDateRange[0] == null || newDateRange[1] != null){
                newDateRange[0] = val;
                newDateRange[1] = null;
            } else {
                newDateRange[1] = val;
            }

            if(newDateRange[0] != null && newDateRange[1] != null && newDateRange[1].isBefore(newDateRange[0])){
                const temp = newDateRange[1];
                newDateRange[1] = newDateRange[0];
                newDateRange[0] = temp;
            }
        }

        setCurrentDateRange(newDateRange);
    }

    const completeSelection = () => {
        setModalVisible(false);

        const finalRange = [currentDateRange[0], currentDateRange[1]];

        if(finalRange[1] == null && field2 != undefined){
            finalRange[1] = finalRange[0];
        }

        updateDateRange(finalRange);
    }

    const years = _.map(_.range(parseInt(selectedYear) - 1, parseInt(selectedYear) + 2), (i) => {
        const yearStyle = {fontWeight: "bold"};

        if(selectedYear == i){
            yearStyle.textDecoration = "underline";
        }

        return <TouchableOpacity key={i} onPress={() =>setSelectedYear(i)} activeOpacity={1}>
            <BasicText style={yearStyle}>{i}</BasicText>
        </TouchableOpacity>;
    });

    const months = _.map(_.range(0, 12), (i) => {
        const highlightStyle = {};
        const fontStyle = {}
        const momentRepresentation = moment();
        momentRepresentation.set("date", 1).set("month", i).set("year", selectedYear);

        if ((currentDateRange[0] != null && momentRepresentation.isSame(currentDateRange[0], "day")) ||
            (currentDateRange[0] != null && currentDateRange[1] != null && momentRepresentation.isBetween(currentDateRange[0], currentDateRange[1])) ||
            (currentDateRange[1] != null && momentRepresentation.isSame(currentDateRange[1], "day"))) {
                highlightStyle.backgroundColor = "#216ba5";
                highlightStyle.borderRadius = "2px";
                fontStyle.color = "white";
        }

        return <Month key={i} onPress={() => setSelectedMonth(i)} activeOpacity={1} style={highlightStyle}>
            <BasicText style={fontStyle}>{momentRepresentation.format("MMM")}</BasicText>
        </Month>;
    })

    return <Modal
        animationType="none"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => {
            setModalVisible(false);
        }}>
            <ModalOpacity onPressOut={()=>setModalVisible(false)} activeOpacity={1}>
                <ModalView onPress={null} activeOpacity={1}>
                    <ModalBody>
                        <YearContainer>
                            <Icon icon="chevron-circle-left" onClick={()=>decrementPeriod()}/>
                            {years}
                            <Icon icon="chevron-circle-right" onClick={()=>incrementPeriod()}/>
                        </YearContainer>
                        <MonthContainer>
                            {months}
                        </MonthContainer>
                        {field2 == null? 
                            null : 
                            <ActionContainer>
                                <TouchableOpacity onPress={()=>completeSelection()} style={{flexBasis: "100%", alignItems: "center"}}>
                                    <BasicText>Done</BasicText>
                                </TouchableOpacity>
                            </ActionContainer>
                        }
                    </ModalBody>
                </ModalView>
        </ModalOpacity>
    </Modal>;
};

const YearContainer = styled.View`
    flex-direction: row;
    flex-basis: auto;
    justify-content: space-between;
    align-items: center;
    border-width: 1px;
    border-color: #aeaeae;
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
    padding: 5px;
    background-color: #f0f0f0;
`;

const MonthContainer = styled.View`
    flex-direction: row;
    flex-wrap: wrap;
    flex-basis: auto;
    justify-content: space-evenly;
    border-width: 1px;
    border-top-width: 0px;
    border-color: #aeaeae;
    background-color: #ffffff;
`;

const ActionContainer = styled.View`
    flex-direction: row;
    flex-wrap: wrap;
    flex-basis: auto;
    justify-content: space-evenly;
    padding-top: 5px;
    padding-bottom: 5px;
    border-width: 1px;
    border-top-width: 0px;
    border-color: #aeaeae;
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
    background-color: #ffffff;
`;

const Month = styled.TouchableOpacity`
    flex-basis: 32%;
    align-items: center;
    margin-top: 5px;
    margin-bottom: 5px;
    height: 30px;
    justify-content: center;
`;

const ModalBody = styled.View`
    z-index: 1;
`;

const ModalOpacity = styled.TouchableOpacity`
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.8);
`;

const ModalView = styled.TouchableOpacity`
    align-items: center;
    shadow-color: #000;
    box-shadow: 0 2px;
    shadow-opacity: 0.25;
    shadow-radius: 4px;
    elevation: 5;
`;

const DisplayText = styled.View`
    justify-content: center;
    align-items: center;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-left: 10px;
    padding-right: 10px;
    margin-right: 5px;
    margin-left: 5px;
    border-radius: 4px;
    border-color: rgb(170, 170, 170);
    border-width: 1px;
    height: 40px;
    min-width: 70px;
`;

export default PeriodPicker;